import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/contexts/SettingsContext";

export default function HeroCollage() {
  const { settings } = useSettings();
  const media = (settings.hero_media || []).slice(0, 5);
  const src = (i) => media[i]?.url || "";
  const has = (i) => Boolean(src(i));

  return (
    <section data-testid="hero-collage" className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-3 md:gap-5">
          {/* Left column of stacked images */}
          <div className="col-span-5 md:col-span-3 flex flex-col gap-3 md:gap-5">
            {has(0) && (
              <div className="hover-scale aspect-[3/4] w-full">
                <img src={src(0)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            )}
            {has(1) && (
              <div className="hover-scale aspect-square w-full mt-6 md:mt-16">
                <img src={src(1)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          {/* Center — title and a tall image */}
          <div className="col-span-7 md:col-span-6 flex flex-col items-center gap-6 md:gap-10 pt-6 md:pt-16">
            <div className="text-center reveal">
              <div className="eyebrow mb-4">A Wedding Portfolio · Est. Since Ever</div>
              <h1 className="font-display text-[10vw] md:text-[5.5vw] leading-[0.95] tracking-tight">
                Weddings <em className="italic text-[color:var(--accent)]">planned</em>
                <br />with taste,
                <br /><em className="italic">detail,</em> and intention.
              </h1>
              <p className="mt-6 max-w-md mx-auto text-sm md:text-base text-[color:var(--text-muted)] leading-relaxed">
                A visual portfolio of celebrations, spaces, stories, and moments curated by our planning team.
              </p>
              <div className="mt-8 flex gap-3 justify-center">
                <Link to="/weddings" data-testid="hero-cta-weddings" className="btn-pill btn-primary">View Weddings</Link>
                <Link to="/reels" data-testid="hero-cta-reels" className="btn-pill btn-outline">Watch Reels</Link>
              </div>
            </div>
            {has(2) && (
              <div className="hover-scale w-full aspect-[4/5] reveal-2 hidden md:block">
                <img src={src(2)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="col-span-12 md:col-span-3 flex flex-col gap-3 md:gap-5 mt-4 md:mt-0">
            <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-5">
              {has(3) && (
                <div className="hover-scale aspect-[4/5] md:aspect-[3/4] w-full md:mt-20">
                  <img src={src(3)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              )}
              {has(4) && (
                <div className="hover-scale aspect-square w-full">
                  <img src={src(4)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom marquee */}
        <div className="mt-16 md:mt-24 border-y border-[color:var(--border)] overflow-hidden">
          <div className="flex whitespace-nowrap ticker py-4">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="flex items-center gap-8 pr-8 eyebrow text-[color:var(--text-muted)]">
                <span>Curated Celebrations</span><span>•</span>
                <span>Design-led Planning</span><span>•</span>
                <span>Hospitality First</span><span>•</span>
                <span>Editorial Detail</span><span>•</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
